import { chatDrawerSelectors } from 'ducks/chatDrawer'
import { wsSelectors } from 'ducks/websocket'
import { Box, keyframes, styled, Typography } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useChatContext } from './ChatContext'
import { useChatSuggestionsPreferences } from './hooks/useChatSuggestionsPreferences'
import { useMessageSuggestions } from './hooks/useMessageSuggestions'

const fadeDown = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const SuggestionsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '20px 16px',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  animation: `${fadeDown} 0.3s ease-out`,
})

const SuggestionsTitle = styled(Typography)({
  fontSize: '14px',
  color: '#666',
  marginBottom: '8px',
  textAlign: 'center',
})

const SuggestionsList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
  maxWidth: '500px',
})

const SuggestionButton = styled('button')({
  padding: '12px 16px',
  borderRadius: '8px',
  border: '1px solid rgba(23, 81, 208, 0.2)',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  color: '#1751D0',
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(23, 81, 208, 0.05)',
    borderColor: 'rgba(23, 81, 208, 0.3)',
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(23, 81, 208, 0.15)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
})

const AnimatedSuggestionButton = styled(SuggestionButton)({
  animation: `${fadeDown} 0.3s ease-out`,
  animationFillMode: 'both',
})

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    const temp = shuffled[i]
    shuffled[i] = shuffled[j]
    shuffled[j] = temp
  }
  return shuffled
}

export const ChatSuggestions: React.FC = () => {
  const suggestionKeys = useMessageSuggestions()
  const translate = useTranslate()
  const chatDrawerOpen = useSelector(chatDrawerSelectors.getChatDrawerOpen)
  const websocketAvailable = useSelector(wsSelectors.getAvailable)
  const messages = useSelector((state: any) => chatDrawerSelectors.getChatDrawerMessages(state))
  const { onSubmit, setMessageText, messageText } = useChatContext()
  const { preferences } = useChatSuggestionsPreferences()
  const pendingSuggestionRef = useRef<string | null>(null)
  const previousDrawerOpenRef = useRef<boolean | null>(null)
  // Initialize animation key to 1 if drawer is already open on mount, so animation plays immediately
  const [animationKey, setAnimationKey] = useState(() => (chatDrawerOpen ? 1 : 0))

  // Track when drawer opens to trigger animation
  useEffect(() => {
    // When drawer transitions from closed to open, trigger animation
    if (chatDrawerOpen && previousDrawerOpenRef.current === false) {
      setAnimationKey((prev) => prev + 1)
    }
    previousDrawerOpenRef.current = chatDrawerOpen
  }, [chatDrawerOpen])

  const suggestions = useMemo(() => {
    const translated = suggestionKeys.map((key) => translate(key))
    return shuffleArray(translated)
  }, [suggestionKeys, translate])

  // Create a stable key based on suggestions content and animation trigger to trigger re-animation
  const suggestionsKey = useMemo(() => {
    return `${suggestionKeys.join('|')}-${animationKey}`
  }, [suggestionKeys, animationKey])

  // Submit when messageText matches the pending suggestion
  useEffect(() => {
    if (pendingSuggestionRef.current && messageText === pendingSuggestionRef.current) {
      pendingSuggestionRef.current = null
      onSubmit()
    }
  }, [messageText, onSubmit])

  const handleSuggestionClick = (suggestion: string) => {
    pendingSuggestionRef.current = suggestion
    setMessageText(suggestion)
  }

  // Don't show component if there are messages, no suggestions, if Ada is disconnected, or if suggestions are disabled
  if (
    messages.length > 0 ||
    suggestions.length === 0 ||
    !websocketAvailable ||
    !preferences.showSuggestionsOnEmptyChat
  ) {
    return null
  }

  return (
    <SuggestionsContainer key={suggestionsKey} id="Ada-Chat-Suggestions">
      <SuggestionsTitle>{translate('Hi there, what can I help you with?')}</SuggestionsTitle>
      <SuggestionsList>
        {suggestions.map((suggestion, index) => (
          <AnimatedSuggestionButton
            key={`${suggestionsKey}-${index}`}
            onClick={() => handleSuggestionClick(suggestion)}
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            {suggestion}
          </AnimatedSuggestionButton>
        ))}
      </SuggestionsList>
    </SuggestionsContainer>
  )
}
